import { INetworkResult } from '@/coreDomainsModule/networkResult';
import { IError } from '../interfaces/IError';
import { getPreference, putPreference } from '@/appLogic/preferenceManage';
import { getOrganizationId } from '@/utils/token';
import { InputExcelInfoByFileType, InputFileType } from '@/coreDomainsModule/entities/inputExcelInfo';
import { IInputExcelInfosRepository } from '../interfaces/IInputExcelInfosRepository';

/**
 * 入力エクセル設定Preferenceの保存形式
 */
type InputExcelInfosPreference = [string, Record<string, InputExcelInfoByFileType>];

/**
 * ファイルタイプ別入力エクセル設定の空オブジェクトを作成する
 */
const createEmptyInputExcelInfoByFileType = (): InputExcelInfoByFileType => ({
  Inventory: undefined,
  SalesResults: undefined,
  Arrival: undefined,
  Order: undefined,
});

/**
 * 入力エクセルの読み込み情報を管理する
 */
export class InputExcelInfosRepository implements IInputExcelInfosRepository {
  private myPreferences;

  /**
   * 入力エクセルの読み込み情報を取得する
   */
  async get(): Promise<INetworkResult<[string, Record<string, InputExcelInfoByFileType>], IError>> {
    const inputExcelInfos = (await getPreference(await getOrganizationId(), 'inputExcelInfos')) as InputExcelInfosPreference | null;

    if (inputExcelInfos != null) {
      return { status: { type: 'success', data: inputExcelInfos } };
    } else {
      return {
        // preference使ってる場合、これしかありえない
        status: {
          type: 'error',
          error: {
            statusCode: 500,
            code: 'notExist',
            message: '入力エクセルの読み込み情報が存在しません',
          },
        },
      };
    }
  }

  /**
   * 利用可能な入力エクセルのキーのリストを取得する
   */
  async getKeys(): Promise<INetworkResult<string[] | null, IError>> {
    const inputExcelInfos = (await getPreference(await getOrganizationId(), 'inputExcelInfos')) as InputExcelInfosPreference | null;
    if (inputExcelInfos != null) {
      return {
        status: {
          type: 'success',
          data: Object.keys(inputExcelInfos[1]),
        },
      };
    } else {
      return {
        status: {
          type: 'success',
          data: null,
        },
      };
    }
  }

  /**
   * 入力エクセルの読み込み情報を登録する
   * @param targetKeyName - 変更するキー名
   * @param registerKeyName - 登録するキー名
   * @param inputFileType - ファイルタイプ
   * @param data - データ
   */
  async put<T extends InputFileType>(
    targetKeyName: string,
    registerKeyName: string,
    inputFileType: T,
    data: NonNullable<InputExcelInfoByFileType[T]>
  ): Promise<INetworkResult<any, IError>> {
    let myPreferences: InputExcelInfosPreference;
    const inputExcelInfos = (await getPreference(await getOrganizationId(), 'inputExcelInfos')) as InputExcelInfosPreference | null; // NOTE: 一旦組織単位で保存する
    let isFirstRegister = false;
    if (inputExcelInfos != null) {
      myPreferences = inputExcelInfos;
      isFirstRegister = false;
    } else {
      isFirstRegister = true;
      myPreferences = [
        registerKeyName, // こいつを初期設定にする
        {
          [registerKeyName]: createEmptyInputExcelInfoByFileType(),
        },
      ]; // 初期化
    }
    const useKey = isFirstRegister ? registerKeyName : targetKeyName;
    if (myPreferences[1][registerKeyName] != null && targetKeyName !== registerKeyName) {
      // 更新後のキーが既に存在する場合はエラー（keyNameが変わっている場合のみ）
      return {
        status: {
          type: 'error',
          error: {
            statusCode: 400,
            code: 'alreadyExist',
            message: '入力エクセルの読み込み情報が既に存在します',
          },
        },
      };
    }
    if (isFirstRegister) {
      myPreferences[0] = useKey;
      myPreferences[1][useKey] = {
        ...createEmptyInputExcelInfoByFileType(),
        [inputFileType]: data,
      };
    } else {
      myPreferences[0] = registerKeyName;
      myPreferences[1][registerKeyName] = {
        ...myPreferences[1][targetKeyName],
        [inputFileType]: data,
      }; // 新しい方を追加

      if (targetKeyName !== registerKeyName) {
        // キー名が変わっている場合は、古い方を消して新しい方を追加する
        delete myPreferences[1][targetKeyName];
      }
    }
    await putPreference(await getOrganizationId(), 'inputExcelInfos', myPreferences);
    return { status: { type: 'success', data: undefined } };
  }

  /**
   * 取り込みで用いるエクセルのキーを登録する
   * @param key - キー
   */
  async putKey(key: string): Promise<INetworkResult<any, IError>> {
    let myPreferences: InputExcelInfosPreference;

    const inputExcelInfos = (await getPreference(await getOrganizationId(), 'inputExcelInfos')) as InputExcelInfosPreference | null; // NOTE: 一旦組織単位で保存する
    if (inputExcelInfos != null) {
      myPreferences = inputExcelInfos;
    } else {
      // ありえない
      return {
        status: {
          type: 'error',
          error: {
            statusCode: 400,
            code: 'notExist',
            message: '入力エクセルの読み込み情報が存在しません',
          },
        },
      };
    }
    inputExcelInfos[0] = key;
    await putPreference(await getOrganizationId(), 'inputExcelInfos', myPreferences);
    return { status: { type: 'success', data: undefined } };
  }
}
